import Result from "@Totum/Main/Infrastructure/Result";
import { requestJson } from "@Totum/Main/Infrastructure/ServiceBase";
import { IQuestion, IQuestionnaireDetails, IQuestionnaireMinimal } from "@Totum/QuestionnaireBuilder/Domain/types";

const API_URL = "/knowledgequestionnaire/api";

/**
 * Fetches all questionnaires.
 */
export const getQuestionnaires = async (): Promise<Result<{ questionnaires: IQuestionnaireMinimal[] }>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/`,
        method: "GET",
    });
};

/**
 * Fetches a specific questionnaire by its ID.
 * @param questionnaireId - The ID of the questionnaire to fetch.
 */
export const getQuestionnaire = async (
    questionnaireId: string
): Promise<Result<{ questionnaire: IQuestionnaireDetails }>> => {
    return await requestJson<{ questionnaire: IQuestionnaireDetails }>({
        url: `${API_URL}/Questionnaire/${questionnaireId}`,
        method: "GET",
    });
};

/**
 * Creates a new questionnaire.
 * @param model - The IQuestionnaireDetails object containing the questionnaire data.
 */
export const createQuestionnaire = async (model: IQuestionnaireDetails): Promise<Result<IQuestionnaireMinimal>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/`,
        method: "POST",
        data: model,
    });
};

/**
 * Updates an existing questionnaire.
 * @param model - The updated questionnaire details data.
 */
export const updateQuestionnaire = async ({
    questionnaireId,
    ...model
}: IQuestionnaireDetails): Promise<Result<IQuestionnaireMinimal>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/${questionnaireId}`,
        method: "PATCH",
        data: model,
    });
};

/**
 * Publishes a questionnaire.
 * @param questionnaireId - The ID of the questionnaire to publish.
 */
export const publishQuestionnaire = async (questionnaireId: string): Promise<Result<boolean>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/${questionnaireId}/publish`,
        method: "POST",
    });
};

/**
 * Deletes a questionnaire.
 * @param questionnaireId - The ID of the questionnaire to delete.
 */
export const deleteQuestionnaire = async (questionnaireId: string): Promise<Result<boolean>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/${questionnaireId}/`,
        method: "DELETE",
    });
};

/**
 * Creates a new question for a questionnaire.
 * @param model - The partial question details for a new question.
 */
export const createQuestion = async (model: IQuestion): Promise<Result<IQuestion>> => {
    return await requestJson({
        url: `${API_URL}/Questionnaire/${model.questionnaireId}/question`,
        method: "POST",
        data: model,
    });
};

/**
 * Updates an existing question for a questionnaire.
 * @param model - The updated question details for a existing question.
 */
export const updateQuestion = async ({ questionId, ...model }: IQuestion): Promise<Result<IQuestion>> => {
    return await requestJson({
        url: `${API_URL}/Question/${questionId}`,
        method: "PUT",
        data: model,
    });
};

/**
 * Deletes a question from a questionnaire.
 * @param questionnaireId - The ID of the questionnaire containing the question.
 * @param questionId - The ID of the question to delete.
 */
export const deleteQuestion = async (questionId: IQuestion["questionId"]): Promise<Result<IQuestion>> => {
    return await requestJson({
        url: `${API_URL}/Question/${questionId}`,
        method: "DELETE",
    });
};

/**
 * Fetches associated questionnaires based on activity or activity category.
 * @param activityId - Optional. The ID of the activity to fetch associations for.
 * @param activityCategoryId - Optional. The ID of the activity category to fetch associations for.
 */
export const getAssociations = async (
    activityId?: string,
    activityCategoryId?: string
): Promise<Result<{ associatedQuestionnaires: IQuestionnaireMinimal[] }>> => {
    const params = `activityId=${activityId}&activityCategoryId=${activityCategoryId}`;
    return await requestJson({
        url: `${API_URL}/Questionnaire/associations?${params}`,
        method: "GET",
    });
};
